import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import { EASE_OUT_EXPO } from '../../../../components/AnimatedSection/AnimatedSection';
import { pastorsData } from '../../../../data/pastorsData';
import './JoinPastorsCarouselSection.scss';

const JoinPastorsCarouselSection = () => {
    const { t } = useTranslation();
    const prefersReducedMotion = useReducedMotion();

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const autoPlayRef = useRef(null);
    const totalPastors = pastorsData.length;

    const fadeUp = (delay, duration = 1.0) =>
        prefersReducedMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 20 },
                  whileInView: { opacity: 1, y: 0 },
                  viewport: { once: true, amount: 0.25 },
                  transition: { duration, delay, ease: EASE_OUT_EXPO },
              };

    const nextSlide = useCallback(() => {
        if (isAnimating) return;
        setIsAnimating(true);
        setCurrentIndex((prev) => (prev + 1) % totalPastors);
        setTimeout(() => setIsAnimating(false), 500);
    }, [isAnimating, totalPastors]);

    const prevSlide = () => {
        if (isAnimating) return;
        setIsAnimating(true);
        setCurrentIndex((prev) => (prev - 1 + totalPastors) % totalPastors);
        setTimeout(() => setIsAnimating(false), 500);
    };

    useEffect(() => {
        if (isAutoPlaying) {
            autoPlayRef.current = setInterval(() => {
                nextSlide();
            }, 4000);
        }

        return () => {
            if (autoPlayRef.current) {
                clearInterval(autoPlayRef.current);
            }
        };
    }, [isAutoPlaying, isAnimating, nextSlide]);

    const handleMouseEnter = () => {
        setIsAutoPlaying(false);
        if (autoPlayRef.current) {
            clearInterval(autoPlayRef.current);
        }
    };

    const handleMouseLeave = () => {
        setIsAutoPlaying(true);
    };

    const current = pastorsData[currentIndex];

    return (
        <section className="join-pastors-carousel">
            <div className="join-pastors-carousel__outer">
                <div className="join-pastors-carousel__container">
                    <div className="join-pastors-carousel__grid">

                        <motion.div className="join-pastors-carousel__image-col" {...fadeUp(0)}>
                            <div
                                className="join-pastors-carousel__photo-container"
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                            >
                                <img
                                    src={current.photo}
                                    alt={t(current.nameKey)}
                                    className="join-pastors-carousel__photo"
                                />
                                <div className="join-pastors-carousel__photo-overlay">
                                    <h3 className="join-pastors-carousel__photo-name">{t(current.nameKey)}</h3>
                                    <span className="join-pastors-carousel__photo-role">{t(current.roleKey)}</span>
                                </div>

                                <button
                                    className="join-pastors-carousel__arrow join-pastors-carousel__arrow--left"
                                    onClick={prevSlide}
                                    disabled={isAnimating}
                                    aria-label={t('aria_previous_pastor')}
                                >
                                    <svg className="join-pastors-carousel__arrow-icon" viewBox="0 0 320 512">
                                        <path d="M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z"></path>
                                    </svg>
                                </button>

                                <button
                                    className="join-pastors-carousel__arrow join-pastors-carousel__arrow--right"
                                    onClick={nextSlide}
                                    disabled={isAnimating}
                                    aria-label={t('aria_next_pastor')}
                                >
                                    <svg className="join-pastors-carousel__arrow-icon" viewBox="0 0 320 512">
                                        <path d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"></path>
                                    </svg>
                                </button>
                            </div>

                            <div className="join-pastors-carousel__indicators">
                                {pastorsData.map((_, idx) => (
                                    <button
                                        key={idx}
                                        className={`join-pastors-carousel__indicator ${idx === currentIndex ? 'active' : ''}`}
                                        onClick={() => {
                                            setIsAutoPlaying(false);
                                            if (autoPlayRef.current) {
                                                clearInterval(autoPlayRef.current);
                                            }
                                            setCurrentIndex(idx);
                                            setTimeout(() => setIsAutoPlaying(true), 3000);
                                        }}
                                    />
                                ))}
                            </div>
                        </motion.div>

                        <div className="join-pastors-carousel__text-col">
                            <motion.h2 className="join-pastors-carousel__title" {...fadeUp(0.15, 1.1)}>{t('our_pastors')}</motion.h2>
                            <motion.p className="join-pastors-carousel__text" {...fadeUp(0.3)}>
                                {t('join_pastors_text_1')}
                            </motion.p>
                            <motion.p className="join-pastors-carousel__text" {...fadeUp(0.45)}>
                                {t('join_pastors_text_2')}
                            </motion.p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default JoinPastorsCarouselSection;
